import type { UserPermission } from '../../../../../hooks/useEditDefaultPermissions';
import type { Role } from '../../../../../hooks/useValidateSession';
import { copyUserPermission } from '../../permissions/permissionCatalog';

/**
 * Admin is not a stored role — it is the `admin` flag on the user — so the
 * flow treats it as a two-value field next to position, the same way the users
 * table's Role column does.
 */
export type InviteRoleValue = 'member' | 'admin';

export const INVITE_ROLE_OPTIONS: Array<{ value: InviteRoleValue; label: string }> = [
    { value: 'member', label: 'Member' },
    { value: 'admin', label: 'Admin' },
];

export type InvitePermissionsMode = 'default' | 'custom';

export const INVITE_PERMISSIONS_OPTIONS: Array<{ value: InvitePermissionsMode; label: string }> = [
    { value: 'default', label: 'Default' },
    { value: 'custom', label: 'Custom' },
];

/** One user being added, before any request goes out. */
export interface InviteDraft {
    id: string;
    email: string;
    /** Empty until picked; an invite cannot be sent without it. */
    position: Role | '';
    locationId: Array<string> | 'all';
    role: InviteRoleValue;
    /** null = follow the position's role defaults. */
    customPermissions: UserPermission | null;
}

let draftCounter = 0;

export const newInviteDraft = (overrides?: Partial<InviteDraft>): InviteDraft => ({
    id: `invite-${++draftCounter}`,
    email: '',
    position: '',
    locationId: 'all',
    role: 'member',
    customPermissions: null,
    ...overrides,
});

/** Deliberately loose: the server is the authority, this only catches typos. */
export const isValidInviteEmail = (email: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

export const invitePermissionsMode = (draft: InviteDraft): InvitePermissionsMode =>
    draft.customPermissions ? 'custom' : 'default';

/** What the Permissions cell reads, matching the users table and detail page. */
export const invitePermissionsLabel = (draft: InviteDraft): string => {
    if (draft.role === 'admin') return 'Admin';
    return draft.customPermissions ? 'Custom' : 'Default';
};

/** The `permissions` field this draft sends: admins get everything. */
export const invitePermissionsPayload = (
    draft: InviteDraft,
): UserPermission | 'admin' | 'default' => {
    if (draft.role === 'admin') return 'admin';
    return draft.customPermissions ?? 'default';
};

/**
 * The set the permissions editor shows: the draft's own overrides once it has
 * them, otherwise a copy of its position's defaults, which is what editing
 * starts from.
 */
export const effectiveInvitePermissions = (
    draft: InviteDraft,
    defaultPermissions: Partial<Record<Role, UserPermission>>,
): UserPermission =>
    draft.customPermissions
        ? copyUserPermission(draft.customPermissions)
        : copyUserPermission(draft.position ? defaultPermissions[draft.position] : undefined);

/** A row is sendable once it has an email, a position, and somewhere to work. */
export const isInviteDraftComplete = (draft: InviteDraft): boolean =>
    isValidInviteEmail(draft.email) &&
    draft.position !== '' &&
    (draft.locationId === 'all' || draft.locationId.length > 0);

/** Rows the user has begun filling in; blank rows are ignored, not invalid. */
export const isInviteDraftStarted = (draft: InviteDraft): boolean =>
    draft.email.trim().length > 0;

/**
 * Invite payloads for the drafts worth sending, one per unique email. A repeat
 * email would be rejected by the server anyway, so the first one wins.
 */
export const buildInvitePayloads = (drafts: InviteDraft[]) => {
    const byEmail = new Map<
        string,
        {
            email: string;
            position: Role;
            locationId: Array<string> | 'all';
            admin?: boolean;
            permissions?: UserPermission | 'admin' | 'default';
        }
    >();

    drafts.filter(isInviteDraftComplete).forEach((draft) => {
        const email = draft.email.trim();
        const key = email.toLowerCase();
        if (byEmail.has(key)) return;
        const permissions = invitePermissionsPayload(draft);
        byEmail.set(key, {
            email,
            position: draft.position as Role,
            locationId: draft.locationId,
            // Both fields are omitted for a plain member on role defaults, so
            // those invites send exactly the body they always did.
            ...(draft.role === 'admin' ? { admin: true } : {}),
            ...(permissions === 'default' ? {} : { permissions }),
        });
    });

    return Array.from(byEmail.values());
};
